import { Skeleton } from "@credit-acceptance/uikit";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import { Button } from "../../components/ui/button";
import { Checkbox } from "../../components/ui/checkbox";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { protocolOf } from "../../lib/url";
import { useApplyDocumentAction } from "./api/useApplyDocumentAction";
import { sendCoord } from "./coord/channel";
import { US_STATES } from "./usStates";
import type { ViewerDocument } from "./viewer.types";

const PDF_TYPE = "application/pdf";
/** MIME types the browser can render natively in an `<img>`. */
const IMAGE_TYPES = new Set(["image/jpeg", "image/jpg", "image/png"]);
/** Schemes safe to render inline (iframe/img). Blocks `javascript:`/`data:`. */
const INLINE_SCHEMES = new Set(["https:", "blob:"]);
/**
 * Schemes safe to expose as a download link. Broader than inline rendering
 * (adds `http:` for dev/preview) while still blocking `javascript:`/`data:`, so
 * the user always keeps a way to retrieve a file the viewer can't preview.
 */
const DOWNLOAD_SCHEMES = new Set(["https:", "http:", "blob:"]);

/**
 * Normalizes a content type for matching: lowercased and stripped of any
 * `; charset=…` parameters (e.g. `Application/PDF; charset=utf-8` → `application/pdf`).
 * @param {string} contentType - The raw content type.
 * @returns {string} The normalized MIME type.
 */
function normalizeContentType(contentType: string): string {
  return contentType.split(";")[0].trim().toLowerCase();
}

/**
 * Whether `src` may be rendered inline (iframe/img). Guards against
 * `javascript:` / `data:text/html` values injected via the payload
 * (defense-in-depth alongside the same-origin BroadcastChannel).
 * @param {string} source - The candidate URL.
 * @returns {boolean} True when the scheme is inline-safe.
 */
function isInlineSafe(source: string): boolean {
  const protocol = protocolOf(source);
  return protocol !== undefined && INLINE_SCHEMES.has(protocol);
}

/**
 * Whether `src` may be offered as a download link.
 * @param {string} source - The candidate URL.
 * @returns {boolean} True when the scheme is safe to link to.
 */
function isDownloadSafe(source: string): boolean {
  const protocol = protocolOf(source);
  return protocol !== undefined && DOWNLOAD_SCHEMES.has(protocol);
}

/**
 * Whether `src` is cross-origin to the app. Fails safe to `false` (treated as
 * same-origin) when the URL can't be parsed or origin is unavailable, so the
 * tighter sandbox is chosen on uncertainty.
 * @param {string} source - The candidate URL.
 * @returns {boolean} True when the source is a different origin than the app.
 */
function isCrossOrigin(source: string): boolean {
  const appOrigin = globalThis.location?.origin;
  try {
    return appOrigin !== undefined && new URL(source, globalThis.location?.href).origin !== appOrigin;
  } catch {
    return false;
  }
}

/**
 * Sandbox attribute for PDFs. Blob URLs need allow-same-origin for the PDF
 * viewer to function; presigned URLs also need it. Other same-origin URLs
 * (if any) omit it for defense-in-depth.
 * @param {string} source - The PDF `src`.
 * @returns {string} The sandbox attribute value.
 */
function pdfSandbox(source: string): string {
  const isBlob = source.startsWith("blob:");
  const needsSameOrigin = isBlob || isCrossOrigin(source);
  return needsSameOrigin
    ? "allow-scripts allow-same-origin allow-downloads allow-popups allow-presentation"
    : "allow-scripts allow-downloads";
}

function handleCancel() {
  window.close();
}

interface MoveDocumentRowProperties {
  appId?: string;
  docId?: string;
  reviewedBy?: string | null;
  /** Called after a successful move to a different approval number. */
  onDocumentMoved?: () => void;
}

// Row of document-move controls shown under the header, only while a document
// is selected. No move-document endpoint exists yet (OR-23048 toolbar
// scaffold) — OK/Cancel move fields are local-only stubs; Cancel resets them.
// Check-in already calls the real BFF action endpoint and locks permanently
// once checked (a document, once checked in, can never be checked out again
// from this row).
function MoveDocumentRow({ appId, docId, reviewedBy, onDocumentMoved }: MoveDocumentRowProperties) {
  const { t } = useTranslation();
  const [moveDocumentTo, setMoveDocumentTo] = useState("");
  const [moveToState, setMoveToState] = useState("");
  const [checkIn, setCheckIn] = useState(Boolean(reviewedBy));
  const [checkInError, setCheckInError] = useState(false);
  const [moveError, setMoveError] = useState(false);
  const checkInLocked = Boolean(reviewedBy) || checkIn;
  const applyDocumentAction = useApplyDocumentAction(appId ?? "", docId ?? "");

  function handleCheckIn(checked: boolean) {
    if (!checked || checkInLocked) {
      return;
    }
    setCheckInError(false);
    setCheckIn(true);
    applyDocumentAction.mutate(
      { checkIn: true },
      {
        // Roll back the optimistic check so the checkbox unlocks and the
        // analyst can retry instead of believing an unreviewed doc is checked in.
        onError: () => {
          setCheckIn(false);
          setCheckInError(true);
        },
      },
    );
  }

  function handleOk() {
    setMoveError(false);
    const movedToApprovalNumber = moveDocumentTo ? Number(moveDocumentTo) : undefined;
    applyDocumentAction.mutate(
      { moveToApprovalNumber: movedToApprovalNumber, moveToState: moveToState || undefined },
      {
        onSuccess: () => {
          // Any successful move (state or approval) clears the viewer back to
          // its waiting state. Only a move to a different approval number also
          // moves the document out of this deal, so only that case broadcasts
          // `doc-moved` to refresh the opener's list.
          onDocumentMoved?.();
          if (movedToApprovalNumber !== undefined && appId && docId) {
            const message = { type: "doc-moved" as const, appId, docId };
            sendCoord(message);
          }
        },
        onError: () => setMoveError(true),
      },
    );
  }

  return (
    <div className="border-border m-2 flex flex-wrap items-center justify-between gap-4 border bg-white px-12.5 py-2">
      <div className="flex items-center gap-2">
        <Label htmlFor="move-document-to" className="text-muted-foreground font-normal">
          {t("documents.viewer.moveDocumentTo")}
        </Label>
        <Input
          id="move-document-to"
          type="text"
          inputMode="numeric"
          value={moveDocumentTo}
          onChange={(event) => setMoveDocumentTo(event.target.value.replaceAll(/\D/g, ""))}
          placeholder={t("documents.viewer.moveDocumentToPlaceholder")}
          className="w-24 sm:w-32"
        />
      </div>

      <div className="flex items-center gap-2">
        <Label htmlFor="move-to-state" className="text-muted-foreground font-normal">
          {t("documents.viewer.moveToState")}
        </Label>
        <Select value={moveToState} onValueChange={setMoveToState}>
          <SelectTrigger id="move-to-state" className="w-40 sm:w-48">
            <SelectValue placeholder={t("documents.viewer.moveToStatePlaceholder")} />
          </SelectTrigger>
          <SelectContent>
            {US_STATES.map((state) => (
              <SelectItem key={state.value} value={state.value}>
                {state.value}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <label className="flex items-center gap-2">
        <span className="text-foreground text-xs">{t("documents.viewer.checkIn")}</span>
        <Checkbox
          checked={checkIn}
          onCheckedChange={(checked) => handleCheckIn(checked === true)}
          disabled={checkInLocked}
        />
      </label>

      {checkInError && (
        <p role="alert" className="text-destructive text-xs">
          {t("documents.viewer.checkInError")}
        </p>
      )}

      {moveError && (
        <p role="alert" className="text-destructive text-xs">
          {t("documents.viewer.moveError")}
        </p>
      )}

      <div className="flex gap-2">
        <Button
          type="button"
          size="xs"
          disabled={applyDocumentAction.isPending || (!moveDocumentTo && !moveToState)}
          onClick={handleOk}
        >
          {t("documents.viewer.ok")}
        </Button>
        <Button type="button" variant="outline" size="xs" onClick={handleCancel}>
          {t("documents.viewer.cancel")}
        </Button>
      </div>
    </div>
  );
}

interface DocumentViewerPageProperties {
  /**
   * The resolved document to render. In production a container passes this from
   * the BroadcastChannel + download-URL resolution; tests and direct callers
   * pass it in. Null renders the waiting state.
   */
  document?: ViewerDocument | null;
  /** Approval id of the active document, forwarded to `MoveDocumentRow`. */
  appId?: string;
  /** Id of the active document, forwarded to `MoveDocumentRow`. */
  docId?: string;
  /** Render the error state (e.g. the download URL could not be resolved). */
  error?: boolean;
  /** Render the loading state while document is being fetched. */
  isLoading?: boolean;
  /** Called after a successful move to a different approval number, forwarded to `MoveDocumentRow`. */
  onDocumentMoved?: () => void;
}

// Renders the document body for the given `contentType`. PDFs load in a
// sandboxed `<iframe>` and JPG/PNG images in an `<img>`; anything else (e.g.
// TIFF, which browsers can't render inline) falls back to a download link.
// An unsafe `src` scheme renders the unsupported fallback with no link.
function DocumentBody({ document }: { document: ViewerDocument }) {
  const { t } = useTranslation();
  const contentType = normalizeContentType(document.contentType);
  const inlineSafe = isInlineSafe(document.src);

  if (inlineSafe && contentType === PDF_TYPE) {
    const isBlob = document.src.startsWith("blob:");
    // #toolbar=0&navpanes=0 works in Chrome/Chromium PDF viewer; behavior may vary in other viewers.
    const pdfUrl = `${document.src}#toolbar=0&navpanes=0`;
    if (isBlob) {
      return <embed src={pdfUrl} type="application/pdf" className="size-full" title={document.fileName} />;
    }
    return (
      <iframe
        src={pdfUrl}
        title={document.fileName}
        className="size-full border-0"
        sandbox={pdfSandbox(document.src)}
      />
    );
  }

  if (inlineSafe && IMAGE_TYPES.has(contentType)) {
    return (
      <div className="flex size-full items-center justify-center overflow-auto p-4">
        <img src={document.src} alt={document.fileName} className="max-h-full max-w-full object-contain" />
      </div>
    );
  }

  return (
    <div className="flex size-full flex-col items-center justify-center gap-3 p-4 text-center">
      <p className="text-muted-foreground text-sm">{t("documents.viewer.unsupported")}</p>
      {isDownloadSafe(document.src) && (
        // `download` is ignored for cross-origin URLs, so this opens the file in
        // a new tab and relies on the object's Content-Disposition for naming.
        <a
          href={document.src}
          download={document.fileName}
          target="_blank"
          rel="noopener noreferrer"
          className="text-primary text-sm underline-offset-4 hover:underline"
        >
          {t("documents.viewer.download")}
        </a>
      )}
    </div>
  );
}

// Generic, static document viewer page (OR-22516 / OR-22558). Presentational:
// it renders whatever resolved `document` it is handed (from the BroadcastChannel
// container in production, or a prop in tests), an error state, or a neutral
// waiting state.
function DocumentViewerPage({
  document = null,
  appId,
  docId,
  error = false,
  isLoading = false,
  onDocumentMoved,
}: DocumentViewerPageProperties) {
  const { t } = useTranslation();

  return (
    <main className="bg-background text-foreground flex h-screen flex-col">
      {document && (
        <MoveDocumentRow
          key={docId}
          appId={appId}
          docId={docId}
          reviewedBy={document.reviewedBy}
          onDocumentMoved={onDocumentMoved}
        />
      )}

      <div className="min-h-0 flex-1">
        {document ? (
          <DocumentBody document={document} />
        ) : isLoading ? (
          <div role="status" aria-label={t("documents.viewer.loading")} className="h-full p-4">
            <Skeleton.Node active className="size-full" />
          </div>
        ) : error ? (
          <div className="flex h-full items-center justify-center p-4">
            <p role="alert" className="text-destructive text-sm">
              {t("documents.viewer.error")}
            </p>
          </div>
        ) : (
          <div className="flex h-full items-center justify-center p-4">
            <p className="text-muted-foreground text-sm">{t("documents.viewer.waiting")}</p>
          </div>
        )}
      </div>
    </main>
  );
}

export default DocumentViewerPage;
